import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { appendSearchParamsToURL } from "@/utils/utils";
import { Card, Tabs, TabsProps } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useUserGetUserProfile } from "../../../common/api/queries";
import { Permission } from "../../../common/security/enums";
import { AgentAttachmentsTable } from "../../agent/components/tables/AgentAttachmentsTable";
import { AgentBrokerNumberTable } from "../../agent/components/tables/AgentBrokerNumberTable";
import { AgentCompetenceTable } from "../../agent/components/tables/AgentCompetenceTable";
import { AgentCompletedEducationTable } from "../../agent/components/tables/AgentCompletedEducationTable";
import { AgentLicenseTable } from "../../agent/components/tables/AgentLicenseTable";
import { AgentMentoringTable } from "../../agent/components/tables/AgentMentoringTable";
import { formatAgentAggregatedName } from "../../agent/utils";
import { useCurrentUser, useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { USER_PROFILE_TABS } from "../../globalsearch/utils";
import NotificationSettingsView from "../../notifications/components/views/NotificationSettingsView";
import UserProfileAgentTabSwitchView from "../components/views/detail/UserProfileAgentTabSwitchView";
import { UserProfileUserAccountView } from "../components/views/detail/UserProfileUserAccountView";
import { UserProfileUserView } from "../components/views/detail/UserProfileUserView";
import { AgentUserProfile, ClientUserProfile } from "../types";
import { isAgentUserProfile } from "../utils";

export const UserProfileContainer = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const urlTabKey = urlParams.get("tab");

  const user = useCurrentUser();
  const { data: userProfile, isError } = useUserGetUserProfile(user?.id);
  const openTotpDeviceFormByDefault = urlParams.get("addTotpDevice") === "true";

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const hasProfileCompetencePermission = useHasPermissions(Permission.PROFILE_COMPETENCE);
  const hasProfileLicensePermission = useHasPermissions(Permission.PROFILE_LICENSE);
  const hasProfileBrokerNumberPermission = useHasPermissions(Permission.PROFILE_BROKER_NUMBER);
  const hasProfileEducationPermission = useHasPermissions(Permission.PROFILE_EDUCATION);
  const hasProfileMentoringPermission = useHasPermissions(Permission.PROFILE_MENTORING);

  const resolveTabKey = () => {
    switch (urlTabKey) {
      case USER_PROFILE_TABS.USER:
      case USER_PROFILE_TABS.ACCOUNT:
      case USER_PROFILE_TABS.ATTACHMENTS:
      case USER_PROFILE_TABS.NOTIFICATION_SETTINGS:
        return urlTabKey;
      case USER_PROFILE_TABS.COMPETENCES:
        return hasProfileCompetencePermission ? urlTabKey : USER_PROFILE_TABS.USER;
      case USER_PROFILE_TABS.LICENSES:
        return hasProfileLicensePermission ? urlTabKey : USER_PROFILE_TABS.USER;
      case USER_PROFILE_TABS.BROKER_NUMBERS:
        return hasProfileBrokerNumberPermission ? urlTabKey : USER_PROFILE_TABS.USER;
      case USER_PROFILE_TABS.EDUCATION:
        return hasProfileEducationPermission && isTopAgentPlus ? urlTabKey : USER_PROFILE_TABS.USER;
      case USER_PROFILE_TABS.MENTORING:
        return hasProfileMentoringPermission ? urlTabKey : USER_PROFILE_TABS.USER;
      default:
        return USER_PROFILE_TABS.USER;
    }
  };

  const tabKey = resolveTabKey();

  const handleTabKeyChange = (tabKey: string) => {
    navigate(appendSearchParamsToURL({ tab: tabKey }), { replace: true });
  };

  const getItems = (userProfile: AgentUserProfile | ClientUserProfile): TabsProps["items"] => {
    const items: TabsProps["items"] = [
      {
        key: USER_PROFILE_TABS.USER,
        icon: <AntIcon type="user" />,
        label: t("user.titles.userData"),
        children: <UserProfileUserView user={userProfile} openTotpDeviceFormByDefault={openTotpDeviceFormByDefault} />,
      },
      {
        key: USER_PROFILE_TABS.ACCOUNT,
        icon: <AntIcon type="solution" />,
        label: t("user.titles.agentData"),
        children: isAgentUserProfile(userProfile) ? (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.agentData")}
            component={(agentId) => (
              <UserProfileUserAccountView
                agent={
                  agentId === userProfile.representingAgent?.id ? userProfile.representingAgent : userProfile.agent
                }
              />
            )}
          />
        ) : undefined,
      },
      {
        key: USER_PROFILE_TABS.ATTACHMENTS,
        icon: <AntIcon type="paper-clip" />,
        label: t("user.titles.attachments"),
        children: isAgentUserProfile(userProfile) ? (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.agentAttachments")}
            component={(agentId) => (
              <AgentAttachmentsTable
                agent={
                  agentId === userProfile.representingAgent?.id ? userProfile.representingAgent : userProfile.agent
                }
                hasUpload
              />
            )}
          />
        ) : undefined,
      },
    ];

    if (hasProfileCompetencePermission && isAgentUserProfile(userProfile)) {
      items.push({
        key: USER_PROFILE_TABS.COMPETENCES,
        icon: <AntIcon type="file-done" />,
        label: t("user.titles.competences"),
        children: (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.agentData")}
            component={(agentId) => (
              <>
                <h2>
                  {`${t("user.titles.competences")} | ${formatAgentAggregatedName(userProfile.representingAgent || userProfile.agent)}`}
                </h2>

                <AgentCompetenceTable agentId={agentId} />
              </>
            )}
          />
        ),
      });
    }

    if (hasProfileLicensePermission && isAgentUserProfile(userProfile)) {
      items.push({
        key: USER_PROFILE_TABS.LICENSES,
        icon: <AntIcon type="bank" />,
        label: t("user.titles.licenses"),
        children: (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.licenses")}
            component={(agentId) => <AgentLicenseTable agentId={agentId} />}
          />
        ),
      });
    }

    if (hasProfileBrokerNumberPermission && isAgentUserProfile(userProfile)) {
      items.push({
        key: USER_PROFILE_TABS.BROKER_NUMBERS,
        icon: <AntIcon type="id-card" />,
        label: t("user.titles.brokerNumbers"),
        children: (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.brokerNumbers")}
            component={(agentId) => <AgentBrokerNumberTable agentId={agentId} />}
          />
        ),
      });
    }

    if (hasProfileEducationPermission && isTopAgentPlus && isAgentUserProfile(userProfile)) {
      items.push({
        key: USER_PROFILE_TABS.EDUCATION,
        icon: <AntIcon type="reconciliation" />,
        label: t("user.titles.education"),
        children: (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.education")}
            component={(agentId) => <AgentCompletedEducationTable agentId={agentId} />}
          />
        ),
      });
    }

    if (hasProfileMentoringPermission && isAgentUserProfile(userProfile)) {
      items.push({
        key: USER_PROFILE_TABS.MENTORING,
        icon: <AntIcon type="projection" />,
        label: t("user.titles.mentoring"),
        children: (
          <UserProfileAgentTabSwitchView
            userProfile={userProfile}
            title={t("user.titles.mentoring")}
            component={(agentId) => <AgentMentoringTable agentId={agentId} />}
          />
        ),
      });
    }

    items.push({
      key: USER_PROFILE_TABS.NOTIFICATION_SETTINGS,
      icon: <AntIcon type="bell" />,
      label: t("user.titles.notifications"),
      children: (
        <>
          <h2>{t("user.titles.notificationSettings")}</h2>
          <NotificationSettingsView />
        </>
      ),
    });

    return items;
  };

  return (
    <ContentWrapper>
      <Card styles={{ body: { padding: "24px 24px 24px 8px" } }}>
        <DisplayWrapper itemLoaded={!!userProfile} isError={isError}>
          {userProfile && (
            <Tabs tabPlacement="start" activeKey={tabKey} onChange={handleTabKeyChange} items={getItems(userProfile)} />
          )}
        </DisplayWrapper>
      </Card>
    </ContentWrapper>
  );
};
